import React, { memo, useRef, useEffect } from 'react';
import { func, string, bool } from 'prop-types';
import classNames from 'classnames';
import { noop } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import { Checkbox } from '@fiverr-private/forms';
import { GuaranteeIcon } from '@fiverr-private/icons';
import { DrawerFooter } from '@fiverr-private/overlay';
import { Icon } from '@fiverr-private/fit';
import MessageControls from '../MessageControls';
import TextareaWrapper from '../TextareaWrapper';
import { selectSendingFlag, useValidationContext } from '../../context/contexts/validation';
import { DimensionContextProvider } from '../../context/contexts/dimension';
import { useBatchBrief } from '../../context/contexts/sendBatchBrief';
import { selectShowSendBatchBrief, useStaticContext } from '../../context/contexts/static';
import { SATISFACTION_GUARANTEE_IS_SHOWN } from '../../constants/biEvents';
import { useReporting } from '../../hooks';
import SubmitButton from './SubmitButton';

import classes from './style.module.scss';

const SendBatchBrief = ({ sendBatchBrief, setSendBatchBrief }) => {
    const title = translate('send_message.auction.title');
    const titleWithEmoji = `📩 ${title}`;
    const body = translate('send_message.auction.info');

    const auctionSectionClassName = classNames(classes.auctionSection, 'flex flex-items-start flex-justify-between m-b-16');
    const handleOnChange = (e) => {
        setSendBatchBrief(e.target.checked);
    };

    return (
        <div className={auctionSectionClassName}>
            <div className="flex flex-col">
                <span className="tbody-5 text-semi-bold">{titleWithEmoji}</span>
                <span className="tbody-6">{body}</span>
            </div>
            <Checkbox className={classes.flex0} value={sendBatchBrief} onChange={handleOnChange}/>
        </div>
    );
};

SendBatchBrief.propTypes = {
    sendBatchBrief: bool,
    setSendBatchBrief: func
};

export const SendMessage = ({
    copyKeyPrefix = 'send_message',
    drawerView,
    onSubmitSuccess = noop,
    onSubmitFailure = noop,
    onSubmit
}) => {
    const context = useValidationContext();
    const staticContext = useStaticContext();
    const { isBatchBrief, setSendBatchBrief } = useBatchBrief();
    const elementRef = useRef();
    const showSendBriefSection = selectShowSendBatchBrief(staticContext);
    const { isSending } = selectSendingFlag(context);
    const { withSatisfactionGuarantee } = staticContext;
    const { sendBiEvent } = useReporting();

    useEffect(() => {
        if (withSatisfactionGuarantee) {
            sendBiEvent({ type: SATISFACTION_GUARANTEE_IS_SHOWN, params: { elementName: 'first_message_drawer' } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const containerClass = classNames(
        classes.messageForm,
        'flex',
        'flex-col',
        { [classes.sending]: isSending }
    );

    const footerClass = classNames(classes.footerSection, 'flex-between', drawerView ? '' : 'p-t-12 p-l-24 p-r-24 p-b-24', {
        'flex-col': showSendBriefSection
    });

    const buttonClasses = classNames('flex flex-justify-between', {
        [classes.flex1]: !showSendBriefSection
    });

    return (
        <div className={containerClass}
            data-testid="send-message-form"
            ref={elementRef}>
            <DimensionContextProvider elementRef={elementRef}>
                <TextareaWrapper drawerView={drawerView}
                    attachmentsClass={classes.attachmentsDrawer}
                    className={classNames(classes.textArea, { [classes.modalView]: !drawerView, [classes.drawerView]: drawerView })}
                    controlClass={classes.controlBox}/>
                <div className={(!drawerView || showSendBriefSection) && footerClass}>
                    {showSendBriefSection &&
                        <SendBatchBrief sendBatchBrief={isBatchBrief} setSendBatchBrief={setSendBatchBrief}/>}
                    {!drawerView ? <span className={buttonClasses}>
                        <MessageControls enableAuction={isBatchBrief} setEnableAuction={setSendBatchBrief}/>
                        <SubmitButton copyKeyPrefix={copyKeyPrefix}
                            drawerView={drawerView}
                            onSubmitSuccess={onSubmitSuccess}
                            onSubmitFailure={onSubmitFailure}
                            onSubmit={onSubmit}/>
                    </span> : <DrawerFooter>
                        <SubmitButton copyKeyPrefix={copyKeyPrefix}
                            drawerView={drawerView}
                            onSubmitSuccess={onSubmitSuccess}
                            onSubmitFailure={onSubmitFailure}
                            onSubmit={onSubmit}/>
                        {
                            withSatisfactionGuarantee &&
                            <div className={classNames(classes.satisfactionGuaranteeContainer,
                                'tbody-6 text-semi-bold co-grey-900 m-t-12 p-t-4 p-b-4 flex flex-justify-center')}>
                                <Icon color="currentColor" className={classes.icon}>
                                    <GuaranteeIcon/>
                                </Icon>
                                {translate('send_message.satisfaction_guarantee')}
                            </div>
                        }

                    </DrawerFooter>
                    }
                </div>
            </DimensionContextProvider>
        </div>
    );
};

SendMessage.propTypes = {
    copyKeyPrefix: string,
    drawerView: bool,
    onSubmitSuccess: func,
    onSubmitFailure: func,
    onSubmit: func
};

export default memo(SendMessage);
